import { z } from 'zod';

const MONEY = /^\d+(\.\d{1,4})?$/;

export const MandobPaySchemeSchema = z.enum(['salary', 'salary_commission']);
export type MandobPayScheme = z.infer<typeof MandobPaySchemeSchema>;

/** A field sales rep = a user + the `mandob` role + this profile. */
export interface MandobProfile {
  id: string;
  userId: string;
  name: string;
  email: string;
  salary: string;
  payScheme: MandobPayScheme;
  commissionRate: number; // percent of collections
  territory: string | null;
  /** Default customer price list (group) — new customers the rep adds inherit it. */
  priceListId: string | null;
  /** That list's name, for the app to say WHICH tier it is quoting. */
  priceListName?: string | null;
  /**
   * The currency that list quotes in. The rep app prices its whole catalogue in
   * the rep's own tier when no customer is picked yet — a قرطاسية rep opens the
   * app to dollar prices, because those are the only prices they can sell at.
   */
  priceListCurrencyCode?: string | null;
  isActive: boolean;
  createdAt: string;
  /**
   * Backend per-currency decimals (code → exponent), attached on the rep's own
   * `GET /mandob/me` only, so the field app formats IQD as whole dinars. Absent
   * on staff-facing list/get responses.
   */
  currencyDecimals?: Record<string, number>;
  /**
   * The currency codes this shop actually USES (Settings → Currencies), for the
   * rep app's currency pickers.
   *
   * Deliberately separate from `currencyDecimals`, which covers the WHOLE
   * catalogue on purpose so a historical amount in a since-retired currency
   * still formats correctly. Reading a picker off that map offered the rep all
   * twenty seeded currencies — AED, JPY, CHF — in a shop that deals in two.
   * Optional: an older API omits it and the client then falls back to the base.
   */
  currencies?: string[];
  /** True when the `tax` module is on — gates the rep's tax note only (attached on /mandob/me). */
  taxEnabled?: boolean;
  /**
   * True when a field sale may NOT drive stock below zero (Settings → Inventory,
   * resolved for the mandob surface — it can be stricter than the shop-wide
   * rule). The catalog already omits empty shelves; this lets the app refuse an
   * item it still holds from a stale cache. Attached on /mandob/me only; absent
   * on an older API ⇒ nothing blocked, exactly as before.
   */
  blockOutOfStock?: boolean;
}

/** Create a mandob: either link an existing user, or create one inline. */
export const MandobCreateSchema = z
  .object({
    userId: z.string().uuid().optional(),
    email: z.string().email().toLowerCase().optional(),
    fullName: z.string().min(1).max(200).optional(),
    password: z.string().min(8).max(200).optional(),
    locale: z.string().max(10).optional(),
    salary: z.string().regex(MONEY).optional(),
    payScheme: MandobPaySchemeSchema.default('salary'),
    commissionRate: z.number().min(0).max(100).optional(),
    territory: z.string().max(120).optional(),
    priceListId: z.string().uuid().nullable().optional(),
  })
  .refine((v) => v.userId || (v.email && v.fullName), {
    message: 'Provide an existing userId, or email + fullName to create one',
  });
export type MandobCreate = z.infer<typeof MandobCreateSchema>;

export const MandobUpdateSchema = z.object({
  salary: z.string().regex(MONEY).optional(),
  payScheme: MandobPaySchemeSchema.optional(),
  commissionRate: z.number().min(0).max(100).optional(),
  territory: z.string().max(120).nullable().optional(),
  priceListId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
});
export type MandobUpdate = z.infer<typeof MandobUpdateSchema>;

export type MandobCommissionType = 'accrue' | 'payout' | 'adjust' | 'reverse';

export interface MandobCommissionEntry {
  id: string;
  type: MandobCommissionType;
  amount: string;
  sourceType: string | null;
  note: string | null;
  createdAt: string;
}

export interface MandobEarnings {
  salary: string;
  payScheme: MandobPayScheme;
  commissionRate: number;
  commissionAccrued: string;   // unpaid accrued commission
  commissionLifetime: string;  // all-time accrued
  recent: MandobCommissionEntry[];
}

// ── Field workspace (the rep's own surface: customers / sell / collect / deliver) ──

/** A customer as seen in the rep's workspace (assignment is a default view, not a hard limit). */
export interface MandobCustomer {
  id: string;
  code: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  isAssigned: boolean; // assigned to this rep
  latitude: number | null; // map pin (reps can't read /parties, so it travels here)
  longitude: number | null;
  /**
   * The customer's price list (customer group), or null. When set, the rep app
   * prices the sale from this group's directly-entered prices (never converted) —
   * matched against each catalog item's `groupPrices[priceListId]`.
   */
  priceListId: string | null;
  /**
   * The group's own currency (`price_lists.currency_code`), or null. The sell
   * screen defaults the cart currency to this so group prices apply as-is.
   */
  priceListCurrencyCode: string | null;
}

/** A rep registers a new customer in the field (assigned to them). */
export const MandobCustomerCreateSchema = z.object({
  displayName: z.string().min(1).max(200),
  phone: z.string().max(32).optional(),
  email: z.string().email().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  /**
   * The client's own id for this customer, which makes the create IDEMPOTENT.
   *
   * Without it a lost response is indistinguishable from a request that never
   * arrived, so the till's outbox replays and a SECOND customer appears for the same
   * person — and any sale queued against the first one stays stuck behind it. Optional
   * so already-installed tills and reps keep working unchanged.
   */
  clientId: z.string().uuid().optional(),
  /** The customer type (price list) the cashier picked, if any. */
  priceListId: z.string().uuid().optional(),
});
export type MandobCustomerCreateInput = z.infer<typeof MandobCustomerCreateSchema>;

/**
 * A rep/till gives a customer a login for the online shop.
 *
 * The password is set here and read out to the customer at the counter. It is
 * deliberately NOT in the WhatsApp message: a chat sits on a phone forever, and the
 * one thing you never want sitting there is a password next to the shop's name and
 * its web address. The message carries the link and the phone number they sign in
 * with, which is what they cannot look up themselves.
 */
export const MandobPortalAccessSchema = z.object({
  password: z.string().min(6).max(200),
  /** Send them the shop link + their login phone over WhatsApp. */
  sendWhatsapp: z.boolean().default(false),
});
export type MandobPortalAccessInput = z.infer<typeof MandobPortalAccessSchema>;

export interface MandobPortalAccessResult {
  enabled: boolean;
  phone: string | null;
  loginUrl: string;
  /** Never fails the request — the account is set up either way. */
  whatsapp: 'sent' | 'skipped' | 'unconfigured' | 'failed';
}

/** An extra selling unit (pack/carton) the rep can pick when ordering. */
/**
 * A browsable category or brand, with its OWN picture.
 *
 * Both apps used to tile these with a photo of some member product, because the
 * catalogue only carried the NAME — so "Pens" showed a random pen and a brand
 * showed a random one of its products instead of its logo. These carry the real
 * image, fetched from /mandob/me/catalog/group-image/:attachmentId.
 */
export interface MandobCatalogGroup {
  id: string;
  name: string;
  /** Attachment id of the group's own image; null when none was uploaded. */
  imageId: string | null;
}

export interface MandobCatalogUnit {
  id: string;
  name: string;
  factor: string;
  /**
   * This pack's own scannable code. Without it a till cannot tell which unit a
   * scan meant and has to ask the cashier — which is the whole point of the
   * barcode being on the carton in the first place.
   */
  barcode?: string | null;
  /**
   * The price to charge for one of these packs — DISCOUNT ALREADY BAKED IN when
   * the `discounts` module has a live rule for it (a unit is a WHOLESALE/جملة
   * line, so it is judged by the wholesale rules, never the retail ones).
   */
  salePrice: string | null;
  /**
   * The price BEFORE the automatic discount, for a "was 10,000" strike-through.
   * Absent (or null) whenever no discount applied — `salePrice` is then already
   * the only price there is. Purely for display: the money is in `salePrice`.
   */
  originalPrice?: string | null;
  /** Whole percent off, for a "-15%" badge. Absent when no discount applied. */
  discountPercent?: number | null;
  /**
   * Per-customer-group prices for THIS packaging unit, keyed by `priceListId`, each
   * in the GROUP's own currency — server-resolved (PricingService.priceListOverrides
   * with this unit's id) and NEVER converted. Present only for the (active) lists
   * where this unit has an EXPLICIT per-unit override; there is no percent branch for
   * units, so an absent/unmatched entry ⇒ the unit's own `salePrice` stands. Same
   * shape + apply-rule as the item-level `MandobCatalogItem.groupPrices`, one level
   * down. Optional so older clients / the offline terminal ignore it (base pricing).
   *
   * Automatic discounts are baked into these too: a group price is what the client
   * actually charges that customer, so a rule that skipped it would simply not
   * exist for grouped customers.
   */
  groupPrices?: Record<string, { unitPrice: string; currencyCode: string | null }>;
}

/** A sellable item for the rep's order builder. */
export interface MandobCatalogItem {
  id: string;
  code: string;
  name: string;
  barcode: string | null; // lets the rep's barcode scanner resolve items via /me/catalog
  /**
   * Extra scannable codes that ALSO resolve to this item (item_barcodes) — a
   * product sold under more than one manufacturer label. The offline terminal
   * matches a scan against `barcode` OR any of these, so a second label works
   * with no network. Optional: an older API omits it and clients then match the
   * primary barcode alone, exactly as before.
   */
  barcodes?: string[];
  /**
   * The item's own (RETAIL/مفرد) price — DISCOUNT ALREADY BAKED IN when the
   * `discounts` module has a live rule for it. Baked rather than sent as a rule
   * set because the offline terminal is a separately released signed binary: an
   * already-deployed till can only learn a new price by being told the number.
   */
  salePrice: string | null;
  /**
   * The price BEFORE the automatic discount, for a "was 10,000" strike-through.
   * Absent (or null) whenever no discount applied. Display only — what is
   * actually charged is `salePrice`.
   */
  originalPrice?: string | null;
  /** Whole percent off, for a "-15%" badge. Absent when no discount applied. */
  discountPercent?: number | null;
  currencyCode: string | null;
  category: string | null;
  brand: string | null;
  /** Id of the first image attachment (fetch via /mandob/me/catalog/:id/image), or null. */
  firstImageId: string | null;
  /**
   * EVERY image attachment, oldest first — `firstImageId` is simply the head of
   * this list, kept as its own field so existing clients (and the offline
   * terminal) are untouched. Fetch one via
   * /mandob/me/catalog/:itemId/image/:attachmentId. Optional: an older API
   * omits it, and the app then shows the single first image.
   */
  imageIds?: string[];
  /** The item's long description, for the product detail sheet. */
  description?: string | null;
  /**
   * The admin's "Featured" checkbox (items.is_featured) — the same flag the
   * storefront's featured section reads. Powers a Featured browse tab.
   */
  isFeatured?: boolean;
  /**
   * Whether stock is counted for this item. The till's hide-zero-stock filter
   * only ever hides a TRACKED item with nothing on hand — a service or an
   * untracked product has no shelf to be empty. Optional/additive: an old API
   * omits it and the till then hides nothing.
   */
  isTracked?: boolean;
  /** Extra packaging units (base unit is the item itself); empty when single-unit. */
  units: MandobCatalogUnit[];
  /**
   * Per-customer-group prices, keyed by `priceListId`, each in the GROUP's own
   * currency — server-resolved (PricingService) and NEVER converted. Present only
   * for the (active) lists where this item has a resolved group price (an explicit
   * list override or the list's percent adjustment). The app applies the SELECTED
   * customer's entry (`groupPrices[customer.priceListId]`) for both the displayed
   * price and the sold line; absent/unmatched ⇒ the item's base price stands.
   * Optional so older clients / the offline terminal ignore it (base pricing).
   *
   * Automatic discounts are baked into these too (see `MandobCatalogUnit.groupPrices`).
   */
  groupPrices?: Record<string, { unitPrice: string; currencyCode: string | null }>;
  /**
   * EVERY category this product is filed in — the primary one first, then the
   * extra shelves the `multi_category` module allows (`item_categories`).
   *
   * Sent only when the product actually has extras, so a shop that files each
   * product once keeps serving byte-identical JSON and a client built before
   * this field keeps browsing by `category` alone. Clients that know it should
   * match AND group on all of these names: a product on two shelves belongs
   * under both, exactly as it does in the ERP and on the storefront.
   */
  categories?: string[];
}

/**
 * A line quantity, SIGNED — unlike every money field here.
 *
 * The counter till pushes its sales through this schema, and at a counter a
 * quantity carries a direction: positive sells, negative takes back (one
 * transaction: buy ten, hand two back), zero quotes a price without charging
 * for it. `MONEY` refused both, so a till that let a cashier type "-2" would
 * have had the whole sync batch rejected — every queued sale behind it jammed —
 * rather than the one line.
 *
 * The PRICE stays unsigned: the direction belongs to the quantity, and a
 * negative price is always a mistake.
 */
const SIGNED_QTY = /^-?\d+(\.\d{1,4})?$/;

const MandobLineSchema = z.object({
  itemId: z.string().uuid().optional(),
  /** Chosen packaging unit (item_units row); only valid alongside itemId. */
  itemUnitId: z.string().uuid().optional(),
  description: z.string().max(500).optional(),
  quantity: z.string().regex(SIGNED_QTY).default('1'),
  unitPrice: z.string().regex(MONEY).default('0'),
});

/** Rep takes an order in the field → a sales order tagged to the rep. */
export const MandobSellSchema = z.object({
  partyId: z.string().uuid(),
  currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
  notes: z.string().max(2000).optional(),
  /**
   * Where the goods leave from — the rep's chosen warehouse (or, for the till,
   * a configured device warehouse). Rides the sales order and is carried onto
   * the invoice at conversion, so posting ships from here. Absent = ship-time
   * inference / tenant default, exactly as before the field existed.
   */
  warehouseId: z.string().uuid().optional(),
  lines: z.array(MandobLineSchema).min(1),
  /**
   * Device-generated UUID. When the offline queue replays a sell after a lost
   * response, the same clientId dedupes it server-side (via pos_sync_receipts)
   * so a retry never creates a duplicate order. Absent ⇒ no dedup (legacy path).
   */
  clientId: z.string().uuid().optional(),
});
export type MandobSellInput = z.infer<typeof MandobSellSchema>;

// ── Offline sales-terminal sync (apps/pos-desktop) ──

/** One offline-captured sale pushed by the terminal — a field sell + a device idempotency key. */
export const MandobSyncSaleSchema = MandobSellSchema.extend({
  /** Device-generated UUID; dedupes a retried push so it never creates a duplicate order. */
  clientId: z.string().uuid(),
  /** Optional order-level discount, applied by the document pipeline on the server. */
  discountType: z.enum(['percent', 'fixed']).optional(),
  discountValue: z.string().regex(MONEY).optional(),
  /**
   * The coupon code the customer quoted, when the terminal priced the sale with
   * one. ADDITIVE and optional: an older terminal omits it and behaves exactly
   * as before.
   *
   * This is NOT the terminal asserting what a coupon is worth — the money that
   * was actually taken rides on `discountType:'fixed'` + `discountValue` above
   * (a statement of fact about the printed receipt, the same shape
   * SalesOrderConvertService uses to preserve an agreed price). This field is
   * the separate CLAIM ("and this is the code they quoted"), which the server
   * independently re-derives, verifies and reconciles at sync. The server stays
   * the only thing that decides whether a redemption is recorded.
   */
  couponCode: z.string().min(1).max(64).optional(),
  /**
   * How the customer paid AT THE COUNTER — and, by its mere presence, the thing
   * that tells this endpoint a TILL is pushing rather than a field rep.
   *
   * Both use it. A rep's sale is an ORDER: goods delivered later, paid later, so
   * it stays a draft sales order and nothing is booked. A till sale is finished
   * business — the customer paid and walked out — so the server follows it with
   * an invoice and, for everything but `credit`, a payment. Without this the
   * takings never reach the books at all: the till was writing draft orders and
   * the shop's own cash account never saw a dinar of it.
   *
   * `credit` means "on account": the invoice is raised (the customer owes it)
   * but no cash moved, so no payment is recorded.
   *
   * Optional, and additive: a terminal built before this omits it and gets
   * exactly the behaviour it had.
   */
  tender: z.enum(['cash', 'card', 'transfer', 'credit']).optional(),
  /**
   * The DEVICE's id for the register shift this sale was rung up during — the
   * same `clientId` the close-out is pushed under, which is what lets the office
   * open a cash-up and see the sales behind it.
   *
   * It has to come from the till because only the till knows. The terminal stamps
   * it at queue time (see its shift.ts) so a clock change cannot move money
   * between shifts, and the server CANNOT re-derive it: sync time is not sale
   * time, and an offline queue drains a whole afternoon's sales into one second.
   *
   * Optional and additive: a terminal built before this omits it, and its sales
   * simply carry no shift — which the office is shown as "not recorded", never
   * guessed at.
   */
  shiftClientId: z.string().uuid().optional(),
  /**
   * PAID IN ANOTHER CURRENCY — a dinar sale paid with dollars, or the reverse.
   *
   * `cashTendered` is what the customer paid, in THEIR currency; `cashChange` the
   * change given back (the till always gives it in the shop's base currency, so
   * it is either the sale's or the tendered currency). Both or neither, and only
   * when the tendered currency is not the sale's — a same-currency sale needs
   * neither and books exactly as before. Cash, or (since till 0.68) a card or
   * transfer charged in the other currency — then the change is 0.
   *
   * The invoice is still settled in its own currency (a payment cannot cross
   * currencies); these two are what the server needs to book the EXCHANGE on the
   * drawer, so the ledger holds the notes that are physically in it and the
   * register close can hand each currency over. Additive: an older till omits them.
   */
  cashTendered: z.object({
    currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
    amount: z.string().regex(MONEY),
  }).optional(),
  cashChange: z.object({
    currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
    amount: z.string().regex(MONEY),
  }).optional(),
});
export type MandobSyncSaleInput = z.infer<typeof MandobSyncSaleSchema>;

/**
 * What the server did with a synced sale's coupon.
 *  - `redeemed`            — verified at sync and worth what was charged; slot consumed.
 *  - `redeemed_adjusted`   — still valid but now worth a different amount; the CHARGED
 *                            amount stands, slot consumed, flagged to the office.
 *  - `honoured_unverified` — unknown/expired/exhausted/wrong-currency at sync; the
 *                            discount still stands (the customer already paid it) but
 *                            NO slot is consumed and the office is alerted by name.
 */
export type MandobSyncCouponStatus = 'redeemed' | 'redeemed_adjusted' | 'honoured_unverified';

/** A batch of offline sales the terminal drains from its outbox on reconnect. */
export const MandobSyncSalesSchema = z.object({
  sales: z.array(MandobSyncSaleSchema).min(1).max(500),
});
export type MandobSyncSalesInput = z.infer<typeof MandobSyncSalesSchema>;

// ---- register shifts pushed by the offline till ----

const SHIFT_MONEY = z.string().regex(/^-?\d+(\.\d{1,4})?$/);

/**
 * A CLOSED register shift, reported by the terminal after the fact.
 *
 * Everything here happened on the device, possibly with no network for the whole
 * shift, so the server records it rather than deciding it. `clientId` is the
 * device's own shift id and the idempotency key: the till re-pushes until it is
 * acknowledged, and a second arrival must correct the first, not double the
 * day's takings.
 */
export const MandobSyncShiftSchema = z.object({
  clientId: z.string().uuid(),
  deviceName: z.string().max(120).optional(),
  openedByName: z.string().max(200).optional(),
  openedAt: z.string().datetime(),
  closedAt: z.string().datetime(),
  currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
  openingFloat: SHIFT_MONEY,
  expectedCash: SHIFT_MONEY,
  countedCash: SHIFT_MONEY,
  salesCount: z.number().int().min(0),
  totalTaken: SHIFT_MONEY,
  cashTaken: SHIFT_MONEY,
  cardTaken: SHIFT_MONEY,
  transferTaken: SHIFT_MONEY,
  creditTaken: SHIFT_MONEY,
  /**
   * Cash handed back for returns during the shift — already netted out of
   * expectedCash. Optional: a till built before refunds came off the drawer
   * sends none, and took none off.
   */
  refundsCash: SHIFT_MONEY.optional(),
  note: z.string().max(500).optional(),
  /**
   * The drawer in every currency OTHER than the shift's own — the dollars in a
   * dinar till. The fields above describe the shift currency alone (a till built
   * before this summed every currency into them, which is why its dollar sales
   * read as dinars). Each currency is counted, handed to the safe and squared up
   * on its own. Additive: an older till omits it.
   */
  foreignCash: z.array(z.object({
    currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
    /** Notes taken in this currency: its own cash sales plus foreign notes for other sales. */
    cashIn: SHIFT_MONEY,
    /** Notes paid out in it: change given and cash refunds. */
    cashOut: SHIFT_MONEY,
    expectedCash: SHIFT_MONEY,
    countedCash: SHIFT_MONEY,
    /** Everything CHARGED in this currency, all tenders. */
    totalTaken: SHIFT_MONEY,
  })).max(10).optional(),
});
export type MandobSyncShiftInput = z.infer<typeof MandobSyncShiftSchema>;

export const MandobSyncShiftsSchema = z.object({
  shifts: z.array(MandobSyncShiftSchema).min(1).max(200),
});
export type MandobSyncShiftsInput = z.infer<typeof MandobSyncShiftsSchema>;

export interface MandobSyncShiftResult {
  clientId: string;
  status: 'ok' | 'error';
  error?: string;
  /**
   * The close-out was recorded but its LEDGER postings failed — the cash handover
   * and/or the over/short. Never `status:'error'`: the till re-pushes anything it
   * is told failed, and re-pushing a shift whose row already landed is how you
   * hand the same takings over twice. Reported so the office can post it by hand.
   */
  postError?: string;
}

/** One shift as the office reads it. */
/**
 * One sale rung up during a shift, as the office reads it back.
 *
 * `docNumber` is the order the sync produced, so this row is a pointer INTO the
 * books, not a second copy of the money — the totals on the shift itself are what
 * the till counted and printed.
 */
export interface TerminalShiftSale {
  /** The sales order, when the push produced one (a payment receipt has none). */
  salesOrderId: string | null;
  invoiceId: string | null;
  docNumber: string;
  /** When the office RECEIVED it — not when it was rung up; the till sells offline. */
  syncedAt: string;
  partyName: string | null;
  currencyCode: string | null;
  total: string | null;
  kind: 'sale' | 'payment' | 'return';
  /**
   * How the customer paid, straight off the till's wire marker. Only for
   * `kind: 'sale'`; a row synced before the tender was recorded reads `cash`,
   * because that has always been the till's default.
   */
  paymentMethod: 'cash' | 'card' | 'transfer' | 'credit' | null;
  /**
   * A cash sale paid in ANOTHER currency's notes (a dollar sale paid in dinars):
   * what was handed over and the change, from the invoice (migration
   * 20261018000001). Null otherwise.
   */
  cashIn?: { currencyCode: string; amount: string } | null;
  cashBack?: { currencyCode: string; amount: string } | null;
  /** How cashIn was paid — null/'cash' = notes, else the tender ('card', 'transfer'…). */
  cashInMethod?: string | null;
}

export interface TerminalShift {
  id: string;
  deviceName: string | null;
  openedByName: string | null;
  openedAt: string;
  closedAt: string;
  currencyCode: string;
  openingFloat: string;
  expectedCash: string;
  countedCash: string;
  /** counted − expected: negative is short, positive is over. */
  difference: string;
  salesCount: number;
  totalTaken: string;
  cashTaken: string;
  cardTaken: string;
  transferTaken: string;
  creditTaken: string;
  /** Cash refunded for returns during the shift (part of why expected < float + cash). */
  refundsCash: string;
  note: string | null;
  createdAt: string;
  /** The drawer in each OTHER currency (see MandobSyncShiftSchema.foreignCash). Empty for most shifts. */
  foreignCash: TerminalShiftForeignCash[];
  /**
   * What the sales the till stamped with this shift add up to, per currency —
   * null for a till too old to stamp them. The till's own figures above stay
   * the authority for the shift currency (they include anything still queued);
   * this is where the OTHER currencies' split by tender, the counts, the
   * returns and the customers' payments come from.
   */
  breakdown?: TerminalShiftBreakdown | null;
}

export interface TerminalShiftBreakdown {
  sales: Array<{ currencyCode: string; method: 'cash' | 'card' | 'transfer' | 'credit'; count: number; total: string }>;
  /** Returns given back at the till (credit notes), as positive amounts. */
  returns: Array<{ currencyCode: string; count: number; total: string }>;
  /** Customers paying off debt at the till. */
  payments: Array<{ currencyCode: string; method: string; count: number; total: string }>;
}

export interface TerminalShiftForeignCash {
  currencyCode: string;
  cashIn: string;
  cashOut: string;
  expectedCash: string;
  countedCash: string;
  /** counted − expected, in this currency. */
  difference: string;
  totalTaken: string;
}

/**
 * A RETURN pushed by the till: some or all of a past sale, given back.
 *
 * Keyed on the SALES ORDER the till already knows (it kept the id when the sale
 * synced), not on an invoice — a cashier holding a receipt has the sale in front
 * of them and should never have to know which invoice it became. The server
 * resolves that.
 *
 * Line numbers, not item ids: the same product can appear twice on one receipt
 * at different prices, and crediting "2 of item X" would be ambiguous about
 * which of them came back.
 */
export const MandobSyncReturnSchema = z.object({
  /** Device-generated UUID; dedupes a retried push so it never credits twice. */
  clientId: z.string().uuid(),
  /** The sales order this return is against (the till's stored serverId). */
  salesOrderId: z.string().uuid().optional(),
  /**
   * OR the invoice itself — for a sale this till never rang up: another till,
   * the office register, an office invoice. Found with the till's return search
   * (GET /mandob/me/returns/search). Exactly one of the two is sent.
   */
  invoiceId: z.string().uuid().optional(),
  reason: z.string().min(1).max(2000),
  lines: z
    .array(z.object({ lineNo: z.number().int().min(1), quantity: z.string().regex(MONEY) }))
    .min(1),
  /**
   * Put the goods back into stock. An explicit choice, not an assumption: a
   * customer returning a damaged item hands back something the shop cannot sell,
   * and restocking it would invent inventory that does not exist.
   */
  restock: z.boolean().optional(),
  warehouseId: z.string().uuid().optional(),
  /**
   * Where the money goes back to.
   *  - `cash`: handed out of the drawer now. The server books a refund (payment
   *    voucher) from the till's cash account for whatever part of the credit the
   *    customer had actually PAID — never more — and the till takes the same
   *    amount off the cash it expects at close.
   *  - `account`: left on the customer's account (a credit, or less debt).
   * Absent = a till built before this: credit only, as it always was — that
   * till does not take refunds off its expected cash, so booking one here would
   * count the same money out twice.
   */
  refund: z.enum(['cash', 'account']).optional(),
  /**
   * A cash refund handed over in ANOTHER currency than the sale's — dinars back
   * for a dollar sale the customer paid in dinars. The refund itself is booked
   * in the sale's currency (it settles the credit note); this is what really
   * left the drawer, so the server books the exchange on it. Absent = paid out
   * in the sale's own currency, as every till before 0.71 did.
   */
  refundIn: z.object({
    currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
    amount: z.string().regex(MONEY),
  }).optional(),
  /** The register shift the refund came out of — the device's own shift id. */
  shiftClientId: z.string().uuid().optional(),
}).refine((r) => !!r.salesOrderId !== !!r.invoiceId, {
  message: 'exactly one of salesOrderId or invoiceId',
  path: ['salesOrderId'],
});
export type MandobSyncReturnInput = z.infer<typeof MandobSyncReturnSchema>;

export const MandobSyncReturnsSchema = z.object({ returns: z.array(MandobSyncReturnSchema).min(1).max(100) });

/**
 * One line of a past sale, and how much of it can still come back.
 *
 * Told by the server rather than derived by the app: only the office knows what
 * has already been credited, from a till, from another device, or by hand. The
 * offline terminal has to guess from its own copy and be clamped on push; a rep
 * app is online when it does this and should simply be told the truth.
 */
export interface MandobReturnableLine {
  lineNo: number;
  itemId: string | null;
  description: string;
  /** How many were sold on this line. */
  quantity: string;
  /** How many have already been credited, across every credit note. */
  credited: string;
  /** quantity − credited, never below zero. */
  remaining: string;
  unitPrice: string;
  /** The packaging unit it was sold in (a carton comes back as a carton). */
  unitName?: string | null;
  unitFactor?: string;
}

export interface MandobReturnable {
  /**
   * What the customer really paid with when it was ANOTHER currency's money —
   * the dinars a dollar sale was paid in, net of change given in those same
   * notes. The till refunds in it by default ("he received dinars").
   */
  paidIn?: { currencyCode: string; amount: string } | null;
  /** The invoice the credit note would be raised against. */
  invoiceId: string;
  invoiceNumber: string;
  currencyCode: string;
  lines: MandobReturnableLine[];
  /** What the customer's paper says: the till's SO-, the register's POS-, else the invoice number. */
  saleNumber?: string;
  source?: MandobReturnSource;
  /** The sale's own date, YYYY-MM-DD. */
  date?: string;
  partyId?: string;
  partyName?: string;
  /**
   * The invoice's money, so the till prices a refund exactly as the server will:
   * each credited line carries its share of the header discount (subtotal vs
   * discountTotal), and cash goes back only for what the customer had paid
   * (amountPaid, which already counts earlier credits and refunds).
   */
  total?: string;
  subtotal?: string;
  discountTotal?: string;
  amountPaid?: string;
}

/** Where a sale was rung up: an offline till, the office register, or an office invoice. */
export type MandobReturnSource = 'till' | 'register' | 'office';

/** One sale the till's return search found. */
export interface MandobReturnCandidate {
  invoiceId: string;
  invoiceNumber: string;
  saleNumber: string;
  source: MandobReturnSource;
  date: string;
  partyName: string;
  currencyCode: string;
  total: string;
  /** The product line that matched a barcode/name search — why this sale is listed. */
  matched?: string;
  /** Everything on it has already come back. Listed (greyed) rather than hidden. */
  fullyReturned: boolean;
}

/** Per-return push result; the terminal marks its outbox row by clientId. */
export interface MandobSyncReturnResult {
  clientId: string;
  status: 'ok' | 'error';
  /** The credit note raised (or the one an earlier push already raised). */
  id?: string;
  docNumber?: string;
  deduped?: boolean;
  error?: string;
  /**
   * The cash refund booked with it (refund: 'cash'), when any part of the credit
   * had been paid. Absent for an account credit, an unpaid sale, an older till.
   */
  refundAmount?: string;
  refundNumber?: string;
}

/** Per-sale push result; the terminal marks its outbox row by clientId. */
export interface MandobSyncSaleResult {
  clientId: string;
  status: 'ok' | 'error';
  id?: string;
  docNumber?: string;
  deduped?: boolean; // already synced earlier — returned the existing order
  error?: string; // problem-details cause code when status==='error'
  /**
   * Outcome of settling the sale's `couponCode`, when it carried one and the
   * discount was non-zero. A coupon problem NEVER sets `status:'error'` — an
   * errored sale is retried forever by the terminal's outbox, so a coupon must
   * not be able to create that state.
   *
   * Also returned on a `deduped` replay, because the till re-pushing is exactly
   * the till whose first response went missing — it would otherwise never learn
   * that its coupon was honoured unverified. Absent when the sale carried no
   * code, so a terminal that never sends one sees the response it always saw.
   */
  couponStatus?: MandobSyncCouponStatus;
  /**
   * Whether a TILL sale was booked — invoiced, and paid when the tender wasn't
   * `credit`. Absent for a rep's order, which is not meant to be booked.
   *
   * Reported per sale rather than swallowed, and NEVER as `status:'error'`: the
   * order itself landed, and an errored sale is retried by the outbox for ever.
   * A sale that took money but failed to reach the books is the one thing a shop
   * must be able to find, so the till surfaces it instead of the office
   * discovering a hole at month end.
   */
  booked?: boolean;
  /** Why booking failed, when `booked` is false (e.g. 'no_cash_account'). */
  bookError?: string;
  /**
   * The refusal's specifics, when the server knows them — WHICH item lacked
   * stock and by how much. The till composes its own localized sentence from
   * these; a bare code at the counter is an error nobody can act on.
   * Additive and optional: an older terminal simply ignores it.
   */
  bookErrorMeta?: { itemName?: string; available?: string; requested?: string };
  /** The cause code behind a non-`redeemed` status (e.g. 'coupon_expired'). */
  couponReason?: string;
}

/**
 * One customer group (price list) in the offline snapshot. The terminal joins
 * `customer.priceListId` → this row for the group's NAME (to show "قرطاسية · USD")
 * and its CURRENCY (the currency the cart must adopt for that customer). Prices
 * themselves live on each item/unit under `groupPrices[priceListId]`.
 */
export interface MandobPriceList {
  id: string;
  name: string;
  /** The group's own currency; null ⇒ prices fall back to the tenant base currency. */
  currencyCode: string | null;
  /** 'percent' lists derive prices from the item's base price (in the item's own currency). */
  adjustmentType: 'none' | 'percent';
  adjustmentValue: string | null;
}

/**
 * One coupon in the terminal's offline snapshot — enough for the till to price
 * a code with NO network, and no more. From this row a terminal reproduces 5 of
 * the server's 8 rules exactly (active-as-of-snapshot, starts_at, ends_at,
 * threshold currency, min spend) plus the whole arithmetic (percent/fixed,
 * max_discount, the fixed-vs-cart currency rule, product/category restrictions).
 *
 * It provably CANNOT check usage_limit vs used_count, per_customer_limit, or a
 * deactivation that happened after the snapshot — which is exactly what the
 * sync-time reconciliation and the office alert exist to cover.
 *
 * The anti-fraud spine: offline, a code that is NOT in this list is REFUSED, so
 * an invented code can never produce a discount.
 */
export interface MandobSyncCoupon {
  id: string;
  code: string;
  name: string;
  discountType: 'percent' | 'fixed';
  discountValue: string;
  /** The coupon's own currency (required for `fixed`), or null. */
  currencyCode: string | null;
  /**
   * The RESOLVED currency the monetary thresholds (minSubtotal/maxDiscount) are
   * denominated in — the coupon's own currency, else the tenant's base currency
   * read from the PLATFORM tenants row. Null when the coupon has no thresholds.
   * Pre-resolved because a terminal cannot see the platform DB, and a split
   * tenant's local `tenants` copy is a stale provision-time snapshot.
   */
  thresholdCurrencyCode: string | null;
  minSubtotal: string | null;
  maxDiscount: string | null;
  startsAt: string | null;
  endsAt: string | null;
  /**
   * Product + category restrictions PRE-EXPANDED to a flat item-id list (every
   * descendant of every restricted category included), because the terminal has
   * no category tree. `null` = the whole order qualifies.
   */
  itemIds: string[] | null;
  /**
   * False when the terminal must not price this code offline (the expansion was
   * too large to ship). Such a code is accepted only while online.
   */
  offlineOk: boolean;
}

/** Full catalogue snapshot the terminal caches locally for offline selling. */
export interface MandobSyncCatalog {
  syncedAt: string;
  items: MandobCatalogItem[];
  customers: MandobCustomer[];
  /**
   * Every ACTIVE customer group + its currency, so a fully offline terminal can
   * price a قرطاسية customer in USD with no network: pick the customer → look up
   * `priceLists[customer.priceListId].currencyCode` → adopt it as the cart
   * currency → read each line's `groupPrices[priceListId]`. Never converted.
   */
  priceLists: MandobPriceList[];
  /** Per-currency display decimals (IQD → 0) so the terminal formats money correctly offline. */
  currencyDecimals: Record<string, number>;
  /** Whether the `tax` module is on (terminal shows pre-tax subtotals + a "tax added on sync" note). */
  taxEnabled: boolean;
  /**
   * Whether the `coupons` module is on. This is a CAPABILITY ASSERTION, not
   * cached data: the terminal shows its coupon UI only when it has seen this
   * true, so an old API (which sends neither field) leaves the feature off and
   * the terminal never sends a `couponCode` the server would silently strip.
   */
  couponsEnabled: boolean;
  /** The shop's categories and brands WITH their own images, for the browse tiles. */
  categories?: MandobCatalogGroup[];
  brands?: MandobCatalogGroup[];
  /**
   * The shop's "hide zero-stock items" policy, already resolved (guard ON and
   * the Settings → Inventory toggle not turned off). The till filters its
   * GRID on this — never its receipts or returns, which must keep naming
   * items however empty the shelf is. Optional/additive like couponsEnabled.
   */
  hideZeroStock?: boolean;
  /** The coupon codes this till may price offline. Empty when the module is off. */
  coupons: MandobSyncCoupon[];
  /**
   * Whether the `discounts` module is on. Like `couponsEnabled` this is a
   * CAPABILITY ASSERTION stated on every pull, not cached data: the terminal
   * shows "was / now" pricing only when it has seen this true, so rolling the
   * module (or the API) back stops the badges on the very next sync instead of
   * leaving a till advertising a sale the shop has ended.
   *
   * It does NOT gate the money. Discounts are already baked into every price in
   * this payload, so a client that ignores this field still charges correctly.
   */
  discountsEnabled: boolean;
  /**
   * Where the shop's stock physically is, so the till can answer "have we got
   * any?" without a network. Names only — the terminal never posts a warehouse.
   * Optional: an older terminal ignores it, and an older API omits it.
   */
  warehouses?: MandobWarehouse[];
  /**
   * On-hand quantity per (item, warehouse), AS OF THIS SYNC. Zero and negative
   * rows are omitted, so an item absent from this list simply has none anywhere.
   *
   * This is a SNAPSHOT, not a live figure, and the terminal must say so: a till
   * that has been offline all morning would otherwise let a cashier promise
   * stock that another till already sold.
   */
  stock?: MandobStockLevel[];
}

/** A stock location, for the till's "available" panel. */
export interface MandobWarehouse {
  id: string;
  name: string;
}

/** One on-hand figure. Quantities are decimal strings like every other amount. */
export interface MandobStockLevel {
  itemId: string;
  warehouseId: string;
  quantity: string;
}

/**
 * What is left of ONE product, read live (GET /mandob/me/catalog/:itemId/stock).
 *
 * The rep app asks per product, on the product page, rather than carrying the
 * whole stock table the offline till syncs: a rep standing in a shop is asking
 * about the item in their hand and is online while they ask, so a live answer is
 * both cheaper and truer than a snapshot they'd have to be warned about.
 *
 * `rows` covers ACTIVE warehouses holding a positive quantity — the same rule the
 * till's snapshot applies — so an empty list means "none anywhere", not "unknown".
 */
export interface MandobItemStock {
  itemId: string;
  /** Sum of `rows`, computed server-side so the client never adds decimals. */
  total: string;
  rows: Array<{ warehouseId: string; warehouseName: string; quantity: string }>;
}

/** Rep collects cash in the field → an inbound payment tagged to the rep (drives commission). */
export const MandobCollectSchema = z.object({
  partyId: z.string().uuid(),
  amount: z.string().regex(MONEY),
  currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
  method: z.string().max(32).optional(),
  reference: z.string().max(64).optional(),
  notes: z.string().max(2000).optional(),
  paymentDate: z.string().optional(),
  /**
   * Device-generated UUID. Dedupes a replayed collection (offline queue retry
   * after a lost response) server-side via pos_sync_receipts, so the retry
   * never posts a duplicate payment. Absent ⇒ no dedup (legacy path).
   */
  clientId: z.string().uuid().optional(),
});
export type MandobCollectInput = z.infer<typeof MandobCollectSchema>;

export interface MandobOrderRow {
  id: string;
  /** Owning customer — lets the app filter a customer's own history from the rep's list. */
  partyId: string;
  docNumber: string;
  partyName: string;
  total: string;
  currencyCode: string;
  status: string;
  docDate: string;
}

export interface MandobCollectionRow {
  id: string;
  partyId: string;
  docNumber: string;
  partyName: string;
  amount: string;
  currencyCode: string;
  paymentDate: string;
}

// ── Customer statement (rep-scoped AR view of one of their customers) ──
// Self-contained mirror of the portal's PortalStatement so the field app can
// consume it through the `@erp/shared` → mandob.ts alias (no cross-file imports).

/**
 * A line in a customer's order history on their profile — any channel, so the
 * rep sees what the SHOP ordered, not only what this rep sold them.
 */
export interface MandobCustomerOrder {
  id: string;
  docNumber: string;
  docDate: string;
  status: string;
  currencyCode: string;
  total: string;
  /** field | pos | online | null (office) — where the order came from. */
  channel: string | null;
}

export interface MandobStatementInvoice {
  id: string;
  docNumber: string;
  docDate: string;
  dueDate: string | null;
  status: string;
  currencyCode: string;
  total: string;
  amountPaid: string;
  balance: string;
  isCreditNote: boolean;
}

export interface MandobStatementPayment {
  id: string;
  docNumber: string;
  paymentDate: string;
  currencyCode: string;
  amount: string;
  method: string | null;
  reference: string | null;
}

export interface MandobStatementAging {
  currencyCode: string;
  current: string;
  days_1_30: string;
  days_31_60: string;
  days_61_90: string;
  days_90_plus: string;
  total: string;
}

/** A customer's open-item AR statement (per-currency, never mixed). */
export interface MandobStatement {
  from: string | null;
  asOf: string;
  summary: Array<{ currencyCode: string; invoiced: string; paid: string; balance: string }>;
  aging: MandobStatementAging[];
  invoices: MandobStatementInvoice[];
  payments: MandobStatementPayment[];
}

/** Office payout run: salary + accrued commission for a mandob. */
export const MandobPayoutSchema = z.object({
  mandobUserId: z.string().uuid(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  /** Override the salary portion (defaults to the profile salary). */
  salaryAmount: z.string().regex(MONEY).optional(),
  note: z.string().max(500).optional(),
});
export type MandobPayoutInput = z.infer<typeof MandobPayoutSchema>;

/** Request body for POST /mandob/:id/payout (the rep is resolved from the URL). */
export const MandobPayoutRunSchema = z.object({
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  salaryAmount: z.string().regex(MONEY).optional(),
  note: z.string().max(500).optional(),
});
export type MandobPayoutRunInput = z.infer<typeof MandobPayoutRunSchema>;

/** A recorded payout (one balanced GL entry: salary + commission expense). */
export interface MandobPayout {
  id: string;
  mandobUserId: string;
  mandobName: string;
  periodStart: string | null;
  periodEnd: string | null;
  salaryAmount: string;
  commissionAmount: string;
  total: string;
  journalEntryId: string | null;
  note: string | null;
  createdAt: string;
}

// ── Office rep profile (`GET /mandob/:id/profile`) — a read-only aggregation of
// everything tied to one rep (sales / collections / deliveries / customers /
// payouts). Totals are ALWAYS per-currency (never summed across currencies).

/** A per-currency money aggregate — a count of docs + their summed total. */
export interface MandobCurrencyTotal {
  currencyCode: string;
  count: number;
  /** Sum of the docs' totals in this currency (as a decimal string). */
  total: string;
}

/** A customer this rep registered (parties.created_by_user_id = the rep). */
export interface MandobAddedCustomer {
  id: string;
  code: string;
  displayName: string;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
}

/** A delivery this rep performed as the assigned driver (dispatch board). */
export interface MandobDeliveryRow {
  id: string;
  docNumber: string;
  status: string;
  /** The source order's total + currency (null if the order is gone). */
  orderTotal: string | null;
  currencyCode: string | null;
  createdAt: string;
  deliveredAt: string | null;
}

export type MandobActivityType = 'sale' | 'collection' | 'delivery' | 'customer' | 'payout';

/** One entry in the rep's merged, newest-first activity feed. */
export interface MandobActivityItem {
  type: MandobActivityType;
  /** Id of the underlying record (order / payment / delivery / party / payout). */
  id: string;
  /** ISO date (or date-only) the activity happened. */
  date: string;
  /** Human label — a doc number, customer name, etc. */
  title: string;
  /** Money amount when the activity carries one (null for e.g. a new customer). */
  amount?: string | null;
  currencyCode?: string | null;
  /** Status tag (e.g. a delivery's status), when applicable. */
  status?: string | null;
}

/**
 * The full aggregated profile of one field rep, for the office view behind each
 * row of /mandob. Read-only; scoped to the tenant AND this rep. Money totals are
 * per-currency (see `MandobCurrencyTotal`); base-currency figures (salary /
 * commission / payouts) format with `baseCurrencyCode`.
 */
export interface MandobFullProfile {
  profile: MandobProfile;
  earnings: MandobEarnings;
  /** Tenant base currency — formats salary / commission / payout figures. */
  baseCurrencyCode: string;
  /** Per-currency display decimals (IQD → 0) for the whole page. */
  currencyDecimals: Record<string, number>;
  sales: {
    count: number;
    byCurrency: MandobCurrencyTotal[];
    recent: MandobOrderRow[];
  };
  collections: {
    count: number;
    byCurrency: MandobCurrencyTotal[];
    recent: MandobCollectionRow[];
  };
  deliveries: {
    count: number;
    delivered: number;
    failed: number;
    recent: MandobDeliveryRow[];
  };
  customers: {
    count: number;
    recent: MandobAddedCustomer[];
  };
  payouts: MandobPayout[];
  /** Merged, newest-first recent-activity feed across every surface above. */
  activity: MandobActivityItem[];
}
